import type { BadgeProps } from '../components/Badge';

/**
 * The leaderboard rank chip's two rules, kept next to each other so the chip's
 * look lives in ONE place for both platforms (see `LeaderboardRankChip`).
 */

/**
 * Podium → DS `Badge` colour. #1 gold, #2 silver, #3 bronze; every rank past
 * #3, and the empty-state placeholder, is the neutral chip.
 */
export function leaderboardRankPodiumColour(
  rank: number | '-',
): NonNullable<BadgeProps['colour']> {
  switch (rank) {
    case 1:
      return 'gold';
    case 2:
      return 'silver';
    case 3:
      return 'bronze';
    default:
      return 'neutral';
  }
}

/**
 * Type-size rule: up to 3 digits (≤ 999) the numeral keeps the row's base type
 * size and the chip simply widens past its 24pt minimum; from 4 digits up it
 * steps down one ramp so even a 6-digit global rank (e.g. 109522) fits the row
 * without truncating.
 *
 * Returns Tailwind text-size classes — valid verbatim on both platforms
 * (NativeWind and web share the ramp).
 */
export function leaderboardRankTextClass(rank: number | '-'): string {
  return typeof rank === 'number' && rank > 999 ? 'text-xs' : 'text-base';
}

/**
 * How wide a leaderboard row's NAME is allowed to run, in points/px.
 *
 * Product rule: a long name wraps onto a second line instead of stretching
 * across the whole row. Left unbounded, a full university name ("Uniwersytet
 * SWPS, Uniwersytet SWPS Uniwersytet SWPS …") ran the entire width of the
 * expanded board as one unbroken line and shoved the MP value against the
 * edge — the row stopped reading as a row. Bounded, the name breaks where a
 * reader expects it to and the value column stays put.
 *
 * One number for both platforms so the two boards can't drift apart.
 */
export const LEADERBOARD_NAME_MAX_W = 280;

/**
 * How many lines that name may take before it is clamped. Two: enough for a
 * wrapped school name, few enough that a row's height stays predictable in the
 * 3-row compact widget.
 */
export const LEADERBOARD_NAME_LINES = 2;
